import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import compressImage from '../utils/imageCompression';
import AppShell from '../components/AppShell';
import ImagePickerButtons from '../components/ImagePickerButtons';
import SaveModal from '../components/SaveModal';
import { storageUrl } from '../utils/urls';

export default function ComputerInspection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ scan_result: '', field_inspection: '', notes: '' });
  const [inspectionState, setInspectionState] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    api.get(`/inspections/${id}`).then((r) => {
      setInspectionState(r.data);
      setAttachments((r.data?.attachments || []).filter((attachment) => attachment?.section === 'computer'));
      if (r.data?.computer_result) {
        setForm({
          scan_result: r.data.computer_result.scan_result || '',
          field_inspection: r.data.computer_result.field_inspection || '',
          notes: r.data.computer_result.notes || '',
        });
      }
    });
  }, [id]);

  const uploadOne = async (rawFile) => {
    const file = await compressImage(rawFile);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('section', 'computer');
    fd.append('allow_multiple', '1');
    fd.append('scan_result', form.scan_result || '');
    fd.append('field_inspection', form.field_inspection || '');
    fd.append('notes', form.notes || '');
    const response = await api.post(`/inspections/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    setAttachments((current) => [response.data, ...current]);
  };

  const upload = async (selected) => {
    const files = (Array.isArray(selected) ? selected : [selected]).filter(Boolean);
    // الرفع بالتوازي: انتظار كل صورة قبل التالية كان يضاعف زمن الانتظار.
    await Promise.all(files.map((file) => uploadOne(file)));
  };

  const removeImage = async (attachment) => {
    if (!attachment?.id) return;
    await api.delete(`/inspections/${id}/attachments/${attachment.id}`);
    setAttachments((current) => current.filter((item) => item.id !== attachment.id));
  };

  const save = async () => {
    await api.post(`/inspections/${id}/computer`, form);
    const res = await api.get(`/inspections/${id}`);
    setInspectionState(res.data);
    setShowModal(true);
  };

  return (
    <AppShell
      title="فحص الكمبيوتر"
      actions={<button className="px-3 py-2 rounded-lg border" onClick={() => navigate(`/inspection/${id}/menu`)}>رجوع للقائمة</button>}
    >
      <div className="space-y-3 bg-white rounded-2xl border p-4">
        <label className="block">
          <span className="text-sm text-gray-600">نتيجة الفحص</span>
          <select
            className="mt-1 w-full rounded-lg border p-2"
            value={form.scan_result}
            onChange={(e) => setForm((s) => ({ ...s, scan_result: e.target.value }))}
          >
            <option value="">غير محدد</option>
            <option value="good">سليم</option>
            <option value="has_faults">يوجد أعطال</option>
            <option value="needs_review">يحتاج مراجعة</option>
          </select>
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">الفحص الميداني</span>
          <textarea className="mt-1 w-full rounded-lg border p-2" rows={4} value={form.field_inspection} onChange={(e) => setForm((s) => ({ ...s, field_inspection: e.target.value }))} />
        </label>

        <label className="block">
          <span className="text-sm text-gray-600">الملاحظات</span>
          <textarea className="mt-1 w-full rounded-lg border p-2" rows={4} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
        </label>

        <ImagePickerButtons onSelect={upload} multiple />

        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((attachment) => (
              <div key={attachment.id} className="relative h-20 w-20 overflow-hidden rounded-lg border bg-slate-100">
                <img src={storageUrl(attachment.file_path)} alt="" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(attachment)}
                  className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-xs font-bold leading-none text-white"
                  aria-label="Remove image"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <button onClick={save} className="rounded-xl bg-primary px-6 py-3 text-white font-bold">حفظ</button>
      </div>

      <SaveModal open={showModal} onClose={() => setShowModal(false)} inspectionId={id} state={inspectionState} />
    </AppShell>
  );
}
